import { apiClient } from "~/services/apiClient";
import type {
  CreateCustomerPayload,
  Customer,
  CustomerTask,
  CustomerNote,
  EmergencyContact,
  PostalCodeValidationResponse,
  UpdateAddressPayload,
  UpdateAssistiveAidsPayload,
  UpdateCustomerPayload,
  UpdateCustomerStatusPayload,
  UpdateEmergencyContactsPayload,
  UpdateMedicalAidPayload,
  UpdateMedicalInfoPayload,
  CustomerDocument,
  DocumentUploadMetadata,
  CustomerDevice,
  CreateCustomerDevicePayload,
} from "~/types/customer";
import { mapDeviceResponse } from "~/services/deviceService";
import type { DeviceWithVoice } from "~/types/device";
import type { SearchResponse } from "~/types/search";
import { toSearchMeta } from "~/types/search";

export async function getCustomers(): Promise<Customer[]> {
  const response = await apiClient.get<
    Customer[] | { data?: Customer[]; customers?: Customer[] }
  >("/api/customers");

  if (Array.isArray(response.data)) {
    return response.data;
  }

  if (Array.isArray(response.data?.data)) {
    return response.data.data;
  }

  return Array.isArray(response.data?.customers) ? response.data.customers : [];
}

export type CustomerSearchParams = {
  search?: string;
  page?: number;
  limit?: number;
  /** Serialized table filters, see ~/utils/tableFilters. */
  filters?: string;
};

export async function searchCustomers(
  params: CustomerSearchParams = {}
): Promise<SearchResponse<Customer>> {
  const response = await apiClient.get<{
    data?: Customer[];
    meta?: unknown;
  }>("/api/customers", { params });
  const data = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    data,
    meta: toSearchMeta(response.data?.meta, {
      query: params.search ?? "",
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      totalCount: data.length,
      resultCount: data.length,
      totalPages: data.length > 0 ? 1 : 0,
    }),
  };
}

/**
 * Values a customer list filter can offer, taken from every customer rather than the page on
 * screen.
 */
export async function getCustomerFilterOptions(field: string): Promise<string[]> {
  const response = await apiClient.get<{ data?: unknown }>(
    "/api/customers/filter-options",
    { params: { field } }
  );
  const data = response.data?.data;
  return Array.isArray(data)
    ? data.filter((value): value is string => typeof value === "string")
    : [];
}

export async function getCustomerById(id: string) {
  const response = await apiClient.get<Customer>(`/api/customers/${id}`);
  return response.data;
}

function parseFilenameFromContentDisposition(
  contentDisposition?: string
): string | null {
  if (!contentDisposition) return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1]?.trim() ?? null;
}

function triggerBlobDownload(blob: Blob, filename: string) {
  if (typeof document === "undefined") return;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export type CustomerProfilePdfExport = {
  blob: Blob;
  filename: string;
};

export async function exportCustomerProfilePdf(
  customerId: string,
  fallbackFilename?: string
): Promise<CustomerProfilePdfExport> {
  const response = await apiClient.get<Blob>(
    `/api/customers/${customerId}/export-pdf`,
    { responseType: "blob" }
  );

  const contentDisposition = response.headers?.["content-disposition"];
  const headerFilename = parseFilenameFromContentDisposition(
    typeof contentDisposition === "string" ? contentDisposition : undefined
  );
  const filename =
    headerFilename ??
    fallbackFilename ??
    `customer-profile-${customerId}.pdf`;

  triggerBlobDownload(response.data, filename);

  return { blob: response.data, filename };
}

export async function createCustomer(payload: CreateCustomerPayload) {
  const response = await apiClient.post<Customer>("/api/customers", payload);
  return response.data;
}

export async function updateCustomer(
  id: string,
  payload: UpdateCustomerPayload
) {
  const response = await apiClient.put<Customer>(
    `/api/customers/${id}`,
    payload
  );
  return response.data;
}

export async function updateCustomerStatus(
  id: string,
  payload: UpdateCustomerStatusPayload
) {
  const response = await apiClient.patch<Customer>(
    `/api/customers/${id}/status`,
    payload
  );
  return response.data;
}

export async function deleteCustomer(id: string) {
  await apiClient.delete(`/api/customers/${id}`);
}

/**
 * Soft-delete/archival behavior for customers.
 * The backend may not support hard deletes, but should allow setting the customer inactive.
 */
export async function archiveCustomer(id: string) {
  await updateCustomerStatus(id, {
    status: "inactive",
    temporaryStatus: null,
    temporaryStatusExpiresAt: null,
  });
}

export type CustomerProfilePresignedUpload = {
  url: string;
  key: string;
  expiresIn?: number;
  requiredHeaders?: Record<string, string>;
};

export async function getCustomerProfileUploadUrl(
  id: string,
  payload: { fileName: string; fileSize: number; contentType: string }
) {
  const response = await apiClient.post<CustomerProfilePresignedUpload>(
    `/api/customers/${id}/profile-picture/presigned-url`,
    payload
  );
  return response.data;
}

export async function completeCustomerProfileUpload(id: string, key: string) {
  const response = await apiClient.post<Customer>(
    `/api/customers/${id}/profile-picture/complete`,
    { key }
  );
  return response.data;
}

export async function getCustomerDocuments(id: string) {
  const response = await apiClient.get<CustomerDocument[]>(
    `/api/customers/${id}/documents`
  );
  return response.data;
}

export async function getCustomerDocumentUploadUrl(
  id: string,
  payload: DocumentUploadMetadata & {
    fileName: string;
    fileSize: number;
    contentType: string;
  }
) {
  const response = await apiClient.post<{ url: string; key: string }>(
    `/api/customers/${id}/documents/presigned-url`,
    payload
  );
  return response.data;
}

export async function completeCustomerDocumentUpload(
  id: string,
  payload: {
    key: string;
    fileName: string;
    fileSize: number;
    contentType: string;
  } & DocumentUploadMetadata
) {
  const response = await apiClient.post<CustomerDocument>(
    `/api/customers/${id}/documents/complete`,
    payload
  );
  return response.data;
}

export async function getCustomerDevices(customerId: string): Promise<CustomerDevice[]> {
  const response = await apiClient.get<unknown>(
    `/api/customers/${encodeURIComponent(customerId)}/devices`
  );

  const data = response.data as unknown;
  if (Array.isArray(data)) {
    return data as CustomerDevice[];
  }

  if (data && typeof data === "object") {
    const maybeDevices = (data as { devices?: unknown }).devices;
    if (Array.isArray(maybeDevices)) {
      return maybeDevices as CustomerDevice[];
    }
  }

  return [];
}

export async function createCustomerDevice(
  customerId: string,
  payload: CreateCustomerDevicePayload
): Promise<DeviceWithVoice> {
  const response = await apiClient.post<unknown>(
    `/api/customers/${encodeURIComponent(customerId)}/devices`,
    payload
  );

  return mapDeviceResponse(response.data);
}

export function deviceWithVoiceToCustomerDevice(
  device: DeviceWithVoice
): CustomerDevice {
  return {
    id: device.id,
    customerId: device.customerId,
    unitId: device.unitId,
    imei: device.imei,
    phoneNumber: device.phoneNumber,
    type: device.type,
    deviceType: device.type,
    typeOtherDescription: device.typeOtherDescription,
    status: device.status,
    activationDate: device.activationDate,
    serialNumber: device.serialNumber,
    lastServiceDate: device.lastServiceDate,
    nextServiceDate: device.nextServiceDate,
    notes: device.notes,
    batteryLevel: device.batteryLevel,
    signalStrength: device.signalStrength,
    lastCheckIn: device.lastCheckIn,
    warnings: device.warnings,
    createdAt: device.createdAt,
    updatedAt: device.updatedAt,
    voiceConfigStatus: device.voiceConfigStatus,
    voiceProvisionedAt: device.voiceProvisionedAt,
    voiceLastError: device.voiceLastError,
    voiceLastAttemptAt: device.voiceLastAttemptAt,
    voiceRetryCount: device.voiceRetryCount,
  };
}

export async function unlinkCustomerDevice(
  customerId: string,
  deviceId: string
): Promise<void> {
  await apiClient.delete(
    `/api/customers/${encodeURIComponent(customerId)}/devices/${encodeURIComponent(
      deviceId
    )}`
  );
}

export async function uploadCustomerDocumentDirect(
  id: string,
  file: File,
  metadata: DocumentUploadMetadata & {
    fileName: string;
    fileSize: number;
    contentType: string;
  },
  onProgress?: (value: number) => void
) {
  const formData = new FormData();
  formData.append("file", file, metadata.fileName);
  formData.append("fileName", metadata.fileName);
  formData.append("fileSize", String(metadata.fileSize));
  formData.append("contentType", metadata.contentType);
  formData.append("originalFileName", metadata.originalFileName);
  formData.append("originalMimeType", metadata.originalMimeType);
  formData.append("originalSizeBytes", String(metadata.originalSizeBytes));
  formData.append("finalFileName", metadata.finalFileName);
  formData.append("finalMimeType", metadata.finalMimeType);
  formData.append("finalSizeBytes", String(metadata.finalSizeBytes));
  formData.append("compressionApplied", metadata.compressionApplied);
  if (metadata.contentEncoding) {
    formData.append("contentEncoding", metadata.contentEncoding);
  }
  if (metadata.sha256) {
    formData.append("sha256", metadata.sha256);
  }

  const config =
    onProgress !== undefined
      ? {
          onUploadProgress: (event: {
            loaded: number;
            total?: number;
          }) => {
            if (!event.total) return;
            const percent = Math.round((event.loaded / event.total) * 100);
            onProgress(percent);
          },
        }
      : undefined;
  const response = await apiClient.post<CustomerDocument>(
    `/api/customers/${id}/documents`,
    formData,
    config
  );
  return response.data;
}

export async function deleteCustomerDocument(
  id: string,
  documentId: string
) {
  await apiClient.delete(`/api/customers/${id}/documents/${documentId}`);
}

export async function updateCustomerAddress(
  id: string,
  payload: UpdateAddressPayload
) {
  const response = await apiClient.put<Customer>(
    `/api/customers/${id}/address`,
    payload
  );
  return response.data;
}

export async function validateCustomerAddress(id: string, address: string) {
  const response = await apiClient.post<Customer>(
    `/api/customers/${id}/validate-address`,
    { address }
  );
  return response.data;
}

export async function validatePostalCode(postalCode: string) {
  const response = await apiClient.post<PostalCodeValidationResponse>(
    "/api/customers/postal-code/validate",
    { postalCode }
  );
  return response.data;
}

export async function updateAssistiveAids(
  id: string,
  payload: UpdateAssistiveAidsPayload
) {
  const response = await apiClient.put<Customer>(
    `/api/customers/${id}/assistive-aids`,
    payload
  );
  return response.data;
}

export async function getEmergencyContacts(customerId: string) {
  const response = await apiClient.get<EmergencyContact[]>(
    `/api/customers/${customerId}/contacts`
  );
  return response.data;
}

export async function updateEmergencyContacts(
  customerId: string,
  payload: UpdateEmergencyContactsPayload
) {
  const response = await apiClient.put<{
    customer: Customer;
    warning?: string;
  }>(`/api/customers/${customerId}/contacts`, payload);
  return response.data;
}

export async function updateMedicalAid(
  customerId: string,
  payload: UpdateMedicalAidPayload
) {
  const response = await apiClient.put<Customer>(
    `/api/customers/${customerId}/medical-aid`,
    payload
  );
  return response.data;
}

export async function updateCustomerMedicalInfo(
  customerId: string,
  payload: UpdateMedicalInfoPayload
) {
  const response = await apiClient.put<Customer>(
    `/api/customers/${customerId}/medical`,
    payload
  );
  return response.data;
}

export async function getCustomerTasks(customerId: string) {
  const response = await apiClient.get<{ tasks: CustomerTask[] }>(
    `/api/customers/${customerId}/tasks`
  );
  return response.data.tasks;
}

export async function createCustomerTask(
  customerId: string,
  payload: { title: string; scheduledAt: string | number }
) {
  const response = await apiClient.post<CustomerTask>(
    `/api/customers/${customerId}/tasks`,
    payload
  );
  return response.data;
}

export async function updateCustomerTask(
  customerId: string,
  taskId: string,
  payload: {
    title?: string;
    scheduledAt?: string | number;
    completed?: boolean;
  }
) {
  const response = await apiClient.patch<CustomerTask>(
    `/api/customers/${customerId}/tasks/${taskId}`,
    payload
  );
  return response.data;
}

export async function getCustomerNotes(customerId: string) {
  const response = await apiClient.get<{ notes: CustomerNote[] }>(
    `/api/customers/${customerId}/notes`
  );
  return response.data.notes;
}

export async function createCustomerNote(
  customerId: string,
  payload: { text: string; urgent?: boolean }
) {
  const response = await apiClient.post<CustomerNote>(
    `/api/customers/${customerId}/notes`,
    payload
  );
  return response.data;
}
